<template>
	<div class="container detail-shell page-content-area px-4 pb-10" data-testid="analytics-dashboard">
		<PageHeader
			title="Analytics"
			subtitle="See how many people watched, for how long, and which content performed best."
			stack-below="lg"
		>
			<template v-if="showDashboard" #meta>
				<span class="text-xs text-surface-7 tabular-nums">{{ updatedAgoLabel }}</span>
			</template>

			<template v-if="showDashboard">
				<!-- Date range -->
				<DropDownMenu
					ref="rangeMenuRef"
					type="outline"
					size="md"
					position="right"
					wrapper-class="md:w-[min(640px,calc(100vw-2rem))] !bg-transparent !shadow-none md:!overflow-visible max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:z-50 max-md:max-h-[85dvh] max-md:!overflow-y-auto max-md:rounded-none max-md:[&_.dtr]:max-w-none max-md:[&_.dtr]:rounded-b-none"
					list-classes="!max-h-none !overflow-visible !py-0"
					custom-dropdown-class="md:mt-1"
					data-testid="analytics-range"
					@visibility-change="onRangeMenuVisibility"
				>
					<template #toggle-button>
						<icon-base :width="14" :height="14" width-view-box="24" height-view-box="24" fill="none" class="mr-1.5 text-surface-7"><icon-calendar-outline /></icon-base>
						<span class="truncate">{{ rangeButtonLabel }}</span>
						<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16" class="ml-1.5 text-surface-7"><icon-chevron-down /></icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem is-custom>
							<DateTimeRangeSelector
								:key="`range-${range.preset}-${range.from.getTime()}-${timezone}`"
								:max-days="30"
								:time-zone="timezone"
								:presets="rangePresetOptions"
								:active-preset-key="range.preset === 'custom' ? null : range.preset"
								:model-value="{ from: range.from, to: range.to }"
								@apply="onRangeApply"
							/>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>

				<!-- Compare -->
				<button
					type="button"
					class="inline-flex h-8 items-center gap-2 rounded-md px-3 text-tiny font-medium ring-1 ring-inset transition-colors"
					:class="compare ? 'bg-main/15 text-white ring-main/60' : 'text-surface-text-1 ring-surface-4 hover:bg-surface-2 hover:text-white'"
					role="switch"
					:aria-checked="compare"
					data-testid="analytics-compare"
					@click="toggleCompare"
				>
					<span class="relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors" :class="compare ? 'bg-main-light' : 'bg-surface-5'" aria-hidden="true">
						<span class="absolute size-3 rounded-full bg-white shadow transition-transform" :class="compare ? 'translate-x-3.5' : 'translate-x-0.5'" />
					</span>
					<span class="whitespace-nowrap">Compare<span class="hidden xl:inline">: previous period</span></span>
				</button>

				<!-- Export -->
				<FormButton type="secondary" size="md" class="hidden md:inline-flex" :disabled="!canExport" data-testid="analytics-export" @click="csvOpen = true">
					<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="mr-1.5 text-surface-8"><icon-download /></icon-base>
					Export CSV
				</FormButton>

				<!-- Refresh -->
				<Tooltip content="Refresh data" wrapper-class="hidden md:inline-flex">
					<FormButton type="light" size="md" is-icon aria-label="Refresh data" :disabled="isRefreshing" data-testid="analytics-refresh" @click="refresh">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" :class="{ 'animate-spin': isRefreshing }">
							<path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v2.4h-2.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</FormButton>
				</Tooltip>

				<!-- Settings (timezone) -->
				<AnalyticsTimezonePicker v-model="timezone" />

				<!-- Mobile overflow: export + refresh -->
				<DropDownMenu is-icon type="light" position="right" aria-label="More actions" class="md:hidden" list-width="w-44">
					<template #toggle-button>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="3" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" /><circle cx="13" cy="8" r="1.5" /></svg>
					</template>
					<template #menu-items>
						<DropDownMenuItem :disabled="!canExport" @click="csvOpen = true">Export CSV</DropDownMenuItem>
						<DropDownMenuItem :disabled="isRefreshing" @click="refresh">Refresh data</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</template>
		</PageHeader>

		<!-- Plan gate -->
		<EmptyState
			v-if="!booting && userLoaded && !isAllowed"
			title="Analytics is available on Live Stream plans"
			subtitle="Upgrade your plan to track plays, unique viewers, watch time and where your audience comes from."
			min-height="50vh"
		>
			<template #icon>
				<icon-base :width="28" :height="28" width-view-box="16" height-view-box="16"><icon-chart /></icon-base>
			</template>
			<template #actions>
				<FormButton size="md" type="main" to="/subscribe">Upgrade plan</FormButton>
			</template>
		</EmptyState>

		<!-- Boot skeleton -->
		<div v-else-if="booting" class="mt-2 flex flex-col gap-4" aria-busy="true">
			<div class="h-10 w-full max-w-xl animate-pulse rounded-lg bg-surface-2" />
			<div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
				<div v-for="i in 5" :key="i" class="h-[124px] animate-pulse rounded-xl bg-surface-2" />
			</div>
			<div class="grid grid-cols-1 gap-4 lg:grid-cols-12">
				<div class="h-[380px] animate-pulse rounded-xl bg-surface-2 lg:col-span-7" />
				<div class="h-[380px] animate-pulse rounded-xl bg-surface-2 lg:col-span-5" />
			</div>
		</div>

		<!-- No content on the account -->
		<EmptyState
			v-else-if="streamsLoaded && !hasAnyContent"
			title="No content yet"
			subtitle="Analytics appear after you publish a live stream or video."
			min-height="50vh"
		>
			<template #icon>
				<icon-base :width="28" :height="28" width-view-box="16" height-view-box="16"><icon-chart /></icon-base>
			</template>
			<template #actions>
				<div class="flex gap-2">
					<FormButton size="md" type="main" to="/dashboard">Go to live streams</FormButton>
					<FormButton size="md" type="secondary" to="/vods">Go to Video Hosting</FormButton>
				</div>
			</template>
		</EmptyState>

		<template v-else>
			<div class="mb-2 mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-surface-7 md:mt-0">
				<span>Times shown in {{ timezoneLabel }}</span>
				<span v-if="range.preset === 'custom'" class="text-surface-6">{{ rangeCaption }}</span>
				<span class="md:hidden tabular-nums">{{ updatedAgoLabel }}</span>
			</div>

			<!-- Deep-link banner -->
			<div v-if="deepLinkItem" class="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-surface-2 px-4 py-3 ring-1 ring-inset ring-white/[0.05]" data-testid="analytics-deeplink-banner">
				<div class="flex min-w-0 flex-1 flex-col gap-0.5">
					<div class="flex min-w-0 items-center gap-2">
						<AnalyticsTypeBadge :type="deepLinkItem.type" />
						<span v-if="isNameResolving(deepLinkItem)" class="h-4 w-32 animate-pulse rounded bg-surface-4" aria-busy="true" aria-label="Loading name" data-testid="analytics-name-skeleton" />
						<span v-else class="truncate text-sm font-medium leading-5 text-surface-text-2" :title="deepLinkItem.name">{{ deepLinkItem.name }}</span>
					</div>
					<div class="text-[11px] leading-4 text-surface-7">Showing analytics for this {{ deepLinkItem.type === 'live' ? 'live stream' : 'video' }} only</div>
				</div>
				<FormButton type="secondary" size="sm" class="shrink-0" @click="clearDeepLink">View all analytics</FormButton>
			</div>

			<AnalyticsScopeBar
				v-else
				:scope="scope"
				:scopes="scopes"
				:items="selectedItems"
				:is-default-selection="isDefaultSelection"
				class="mb-5"
				@update:scope="setScope"
				@open-picker="pickerOpen = true"
				@remove="removeSelected"
				@clear="clearSelection"
			/>

			<!-- Nothing in scope (the skeletons below cover the default lookup) -->
			<EmptyState
				v-if="!effectiveIds.length && !resolvingDefaults"
				title="Choose content to see analytics"
				:subtitle="scope === 'vod' ? 'Pick one or more videos to compare their performance.' : scope === 'live' ? 'Pick one or more live streams to compare their performance.' : 'Pick up to 10 live streams or videos to compare.'"
				min-height="40vh"
			>
				<template #actions>
					<FormButton size="md" type="main" @click="pickerOpen = true">Choose content</FormButton>
				</template>
			</EmptyState>

			<template v-else>
				<!-- KPI row -->
				<div class="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="analytics-kpis">
					<AnalyticsKpiCard
						v-for="(m, index) in metrics"
						:key="m.key"
						:class="index === metrics.length - 1 ? 'col-span-2 lg:col-span-1' : ''"
						:metric-key="m.key"
						:label="m.label"
						:definition="m.definition"
						:value="kpiDisplay(m.key).value"
						:value-title="kpiDisplay(m.key).title"
						:loading="kpiDisplay(m.key).loading"
						:error="kpiDisplay(m.key).error"
						:sparkline="kpiDisplay(m.key).sparkline"
						:compare="compare"
						:compare-loading="kpiDisplay(m.key).compareLoading"
						:delta="kpiDisplay(m.key).delta"
						:compare-suffix="compareSuffix"
						:range-caption="rangeShortLabel"
						@retry="retryKpi(m.key)"
					/>
				</div>

				<!-- Leaderboard: primary element, full width so all seven columns fit inside the 1230px page container. -->
				<AnalyticsLeaderboard
					v-if="showLeaderboard"
					class="mt-4"
					:rows="leaderboardRows"
					:state="leaderboardState"
					:empty-title="quietEmptyCopy.title"
					:empty-text="quietEmptyCopy.text"
					:subtitle="leaderboardSubtitle"
					:subtitle-link="leaderboardSubtitleLink"
					:sort="leaderboardSort"
					:loading-columns="leaderboardLoadingColumns"
					:loaded-columns="leaderboardLoadedColumns"
					:failed-columns="leaderboardFailedColumns"
					@update:sort="onLeaderboardSort"
					@request-column="loadLeaderboardSecondary"
					@retry-column="retryLeaderboardColumn"
					@open="openContent"
					@retry="retryLeaderboard"
					@subtitle-link="pickerOpen = true"
				/>

				<!-- Trend + audience -->
				<div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
					<AnalyticsTrendChart
						class="lg:col-span-7"
						:metric="trendMetric"
						:series="trendSeries.current"
						:previous-series="trendSeries.previous"
						:compare="compare"
						:interval="interval"
						:timezone="timezone"
						:state="trendState"
						:empty-title="quietEmptyCopy.title"
						:empty-text="quietEmptyCopy.text"
						:zoom-reset-label="zoomResetLabel"
						@update:metric="setTrendMetric"
						@retry="retryTrend"
						@zoom="onTrendZoom"
						@reset-zoom="resetTrendZoom"
					/>
					<AnalyticsAudienceCard
						class="lg:col-span-5"
						:tab="audienceTab"
						:rows="audienceRows"
						:total="audienceTotal"
						:state="audienceState"
						:empty-title="quietEmptyCopy.title"
						:empty-text="quietEmptyCopy.text"
						@update:tab="setAudienceTab"
						@retry="retryAudience"
						@export="exportAudienceCsv"
					/>
				</div>
			</template>
		</template>

		<AnalyticsSelectStreamModal
			v-model="pickerOpen"
			:live="selectedLiveIds"
			:vod="selectedVodIds"
			@apply="onPickerApply"
		/>

		<CsvDialog
			v-model="csvOpen"
			:range-label="rangeShortLabel"
			:timezone="timezoneLabel"
			:trend-metric-label="trendMetricLabel"
			:has-trend="trendSeries.current.length > 0"
			:has-leaderboard="showLeaderboard && leaderboardRows.length > 0"
			:has-compare="compare"
			@export="exportCsv"
		/>
	</div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import csvDownload from 'json-to-csv-export';

import PageHeader from '@/components/ui/PageHeader.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import DateTimeRangeSelector from '@/components/Atoms/DateTimeRangeSelector.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import IconCalendarOutline from '@/components/icon/IconCalendarOutline.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconChart from '@/components/icon/IconChart.vue';
import AnalyticsSelectStreamModal from '@/components/modals/AnalyticsSelectStreamModal.vue';
import AnalyticsTimezonePicker from '@/components/Analytics/AnalyticsTimezonePicker.vue';
import AnalyticsScopeBar from '@/components/Analytics/AnalyticsScopeBar.vue';
import AnalyticsTypeBadge from '@/components/Analytics/AnalyticsTypeBadge.vue';
import AnalyticsKpiCard from '@/components/Analytics/AnalyticsKpiCard.vue';
import AnalyticsLeaderboard from '@/components/Analytics/AnalyticsLeaderboard.vue';
import AnalyticsTrendChart from '@/components/Analytics/AnalyticsTrendChart.vue';
import AnalyticsAudienceCard from '@/components/Analytics/AnalyticsAudienceCard.vue';
import CsvDialog from './CsvDialog.vue';

import StreamService from '@/services/StreamService';
import { buildDefaultSelection, describeDefaultSelection, findVodFiles } from '@/components/Analytics/analyticsDefaultSelection';
import IntegrationService from '@/services/IntegrationService';
import { track, EVENTS } from '@/lib/tracking';
import { useIntercomLauncher } from '@/composables/useIntercomLauncher';

import {
	METRICS, METRIC_BY_KEY, DEFAULT_TREND_METRIC, AUDIENCE_TABS, AUDIENCE_TAB_BY_KEY, SCOPES,
	MAX_SELECTED_CONTENT, LEADERBOARD_COLUMNS, LEADERBOARD_PRIMARY_COLUMN, LEADERBOARD_REQUEST_COLUMNS,
	sortLeaderboard, topWithOther, metricDependencies, avgViewTimeMs, deriveAvgViewTimeSeries, withDerivedMetrics,
} from '@/components/Analytics/analyticsMetrics';
import {
	RANGE_PRESETS, DEFAULT_RANGE_PRESET, resolveRange, pickInterval, intervalMs, previousPeriod,
	compareLabel, rangeLabel, rangeToQuery, rangeFromQuery, seriesSum, seriesMax,
} from '@/components/Analytics/analyticsRange';
import {
	getBrowserTimeZone, loadStoredTimeZone, storeTimeZone, formatTimeZoneLabel, formatInTz, formatBucketIso, isValidTimeZone,
} from '@/components/Analytics/analyticsTimezone';
import {
	formatMetricValue, formatNumber, formatDelta, formatDurationHms, formatUpdatedAgo,
} from '@/components/Analytics/analyticsFormatters';
import { formatLanguage, formatDeviceClass, formatDimensionValue } from '@/components/Analytics/analyticsLabels';
import {
	buildContentIndex, resolveContent, filterIdsByScope, normalizeContentId, vodFileKey, unresolvedLabel,
} from '@/components/Analytics/analyticsContentIndex';
import {
	fetchAnalytics, clearAnalyticsCache, parseSeries, parseGrouped, parseTotal, fillSeries,
} from '@/components/Analytics/useAnalyticsQuery';
import { perfMark, perfReset } from '@/components/Analytics/analyticsPerf';

const store = useStore();
const route = useRoute();
const router = useRouter();

const metrics = METRICS;
const scopes = SCOPES;
const FILTER_DEBOUNCE_MS = 500;
// Secondary leaderboard columns load this long after the table first paints.
const LEADERBOARD_IDLE_MS = 1000;
const LEADERBOARD_STAGGER_MS = 250; // gap between the deferred column requests
const LEADERBOARD_RETRY_MS = 2000; // one automatic retry for a column that came back empty
const ANALYTICS_PACKAGE_IDS = ['5e8db3dcc3db0533322db725', '6362114709950a6a642540c3'];

// ------------------------------------------------------------------ gate
// The plan gate is resolved from the User store, but the first Bitmovin
// requests do not wait for it: the dashboard renders optimistically and the
// gate replaces it only if the account turns out not to be entitled.
const userLoaded = ref(false);
const subscriptions = computed(() => store.getters['User/subscriptions'] || []);
const isAllowed = computed(() => subscriptions.value.some((p) => {
	if (!p?.enabled) return false;
	if (p.category === 'live') return true;
	return ANALYTICS_PACKAGE_IDS.includes(p.package?._id);
}));

// -------------------------------------------------------------- timezone
const timezone = ref(loadStoredTimeZone() || getBrowserTimeZone());
const timezoneLabel = computed(() => formatTimeZoneLabel(timezone.value));

// --------------------------------------------------------------- filters
const booting = ref(true);
const showDashboard = computed(() => !booting.value && (isAllowed.value || !userLoaded.value));
const range = ref(resolveRange({ preset: DEFAULT_RANGE_PRESET, tz: timezone.value }));
const scope = ref('all');
const compare = ref(false);
const selectedIds = ref([]);
const isDefaultSelection = ref(true);
const trendMetric = ref(DEFAULT_TREND_METRIC);
const audienceTab = ref(AUDIENCE_TABS[0].key);
const pickerOpen = ref(false);
const csvOpen = ref(false);
const rangeMenuRef = ref(null);

// Below md the range menu is a fixed bottom sheet whose Apply button sits
// under the Intercom launcher.
const { setSheetOpen: setRangeSheetOpen } = useIntercomLauncher();
const onRangeMenuVisibility = (open) => {
	setRangeSheetOpen(open && window.matchMedia?.('(max-width: 767px)').matches);
};

const rangePresetOptions = RANGE_PRESETS.filter((p) => p.key !== 'custom').map((p) => ({ label: p.label, key: p.key }));

const interval = computed(() => pickInterval(range.value.from, range.value.to));
const prevRange = computed(() => previousPeriod(range.value));
const compareSuffix = computed(() => compareLabel(range.value));
const rangeShortLabel = computed(() => rangeLabel(range.value, (d) => formatInTz(d, timezone.value, 'date')));
const rangeButtonLabel = computed(() => rangeShortLabel.value);
const rangeCaption = computed(() => `${formatInTz(range.value.from, timezone.value, 'full')} to ${formatInTz(range.value.to, timezone.value, 'full')}`);

// --------------------------------------------------------------- content
const streamsLoaded = computed(() => !!store.state.Streams?.isLoaded);
const liveStreams = computed(() => store.getters['Streams/allInOneStreams'] || []);
const vodFolders = computed(() => store.getters['Streams/vodStreams'] || []);
// Every keyed non-VOD stream names its analytics id (a deep link may come
// from a stream that is not in the all-in-one list); the default selection
// still only draws from `liveStreams`.
const namedStreams = computed(() => (store.state.Streams?.streams || []).filter((s) => s?.key && s.type !== 'vod'));
const knownVodFiles = ref([]);
// Titles handed over by the caller (router state or ?title=) so a deep link
// shows its name before the stores arrive. Real store data wins once present.
const seededNames = ref({}); // id -> { name, type }
const contentIndex = computed(() => {
	const index = buildContentIndex(namedStreams.value, knownVodFiles.value);
	for (const [id, seed] of Object.entries(seededNames.value)) {
		if (!index.has(id)) index.set(id, { id, name: seed.name, type: seed.type || (id.startsWith('live_') ? 'live' : 'vod'), seeded: true });
	}
	return index;
});
// Names are still on their way while the Streams store loads or a deep-link
// VOD lookup is in flight. Until then an unresolved item shows a skeleton
// instead of its raw id; afterwards it falls back to "Untitled …".
const vodNamesPending = ref(false);
const namesResolving = computed(() => !streamsLoaded.value || vodNamesPending.value);
function isNameResolving(item) {
	return !!item?.unresolved && namesResolving.value;
}
const hasAnyContent = computed(() => liveStreams.value.length > 0 || vodFolders.value.length > 0);

const deepLinkId = computed(() => (route.params.videoId ? normalizeContentId(route.params.videoId) : null));
const deepLinkItem = computed(() => {
	if (!deepLinkId.value) return null;
	const item = resolveContent(contentIndex.value, deepLinkId.value);
	// ?stream=true is set by every live-stream entry point, so an id nothing
	// could name yet still gets the Live badge rather than a Video one.
	if (item.unresolved && item.type !== 'live' && route.query.stream === 'true') return { ...item, type: 'live', name: unresolvedLabel('live') };
	return item;
});

const selectedItems = computed(() => selectedIds.value.map((id) => {
	const item = resolveContent(contentIndex.value, id);
	return isNameResolving(item) ? { ...item, nameResolving: true } : item;
}));
const selectedLiveIds = computed(() => selectedItems.value.filter((i) => i.type === 'live').map((i) => i.id));
const selectedVodIds = computed(() => selectedItems.value.filter((i) => i.type !== 'live').map((i) => i.id));
// Ids from the URL are queried right away; the scope filter needs the content
// index, so it only applies once the Streams store has arrived. A deep link
// always queries exactly its item.
const effectiveIds = computed(() => {
	if (deepLinkId.value) return [deepLinkId.value];
	const ids = streamsLoaded.value ? filterIdsByScope(contentIndex.value, selectedIds.value, scope.value) : selectedIds.value.slice();
	return ids.slice(0, MAX_SELECTED_CONTENT);
});
const showLeaderboard = computed(() => !deepLinkId.value && (effectiveIds.value.length > 1 || resolvingDefaults.value));

// Default selection = most recent items in the active scope (live from the
// store; VOD files enumerated from the most recent folders, Castr API only).
// `defaultMix` feeds the leaderboard subtitle; `resolvingDefaults` keeps the
// panels in their skeleton state instead of flashing "Choose content".
const defaultMix = ref({ liveCount: 0, vodCount: 0 });
const resolvingDefaults = ref(false);
let defaultToken = 0;

async function applyDefaultSelection() {
	const token = ++defaultToken;
	resolvingDefaults.value = true;
	let result = { ids: [], liveCount: 0, vodCount: 0, vodFiles: [] };
	try {
		result = await buildDefaultSelection({ scope: scope.value, liveStreams: liveStreams.value, vodFolders: vodFolders.value, limit: MAX_SELECTED_CONTENT });
	} catch (e) {
		// Fall through to an empty default; the user can still pick content.
	}
	// A newer call (scope change, route change) or an explicit pick wins.
	if (token !== defaultToken) return false;
	if (result.vodFiles.length) knownVodFiles.value = mergeVodFiles(knownVodFiles.value, result.vodFiles);
	selectedIds.value = result.ids;
	defaultMix.value = { liveCount: result.liveCount, vodCount: result.vodCount };
	isDefaultSelection.value = true;
	resolvingDefaults.value = false;
	return true;
}

function cancelDefaultResolution() {
	defaultToken += 1;
	resolvingDefaults.value = false;
}

// ------------------------------------------------------------- data state
// Everything below is scoped to the current filters (see filtersStamp);
// changing filters resets it.
const totals = reactive({ current: {}, previous: {} }); // metric -> number|null|undefined
const totalState = reactive({ current: {}, previous: {} }); // metric -> 'loading'|'ready'|'error'
const series = reactive({ current: {}, previous: {} }); // metric -> [[ts, v]] zero-filled
const seriesState = reactive({ current: {}, previous: {} });
// `failed` holds secondary metrics whose grouped request came back empty or
// threw while the primary column had rows; they show a retry in the header.
const leaderboardData = reactive({ metrics: {}, loaded: [], loading: [], failed: [], state: 'loading', error: false });
const leaderboardSort = ref({ column: LEADERBOARD_PRIMARY_COLUMN, direction: 'desc' });
const audienceData = reactive({}); // tab -> { rows, total, state }
const lastUpdated = ref(null);
const isRefreshing = ref(false);
const countryNames = ref({});
let idleTimer = null;
let columnTimers = []; // staggered secondary dispatch + one automatic retry per column
let debounceTimer = null;
let tickTimer = null;
const nowTick = ref(Date.now());
let viewedTracked = false;
let landingStamp = null; // filtersStamp() the current data belongs to

// What the landing fetches for the KPI row; everything else is derived.
const LANDING_SERIES = ['play', 'concurrentViewers'];
const LANDING_TOTALS = ['totalHoursWatched', 'uniqueUsers'];

function clearColumnTimers() {
	for (const t of columnTimers) clearTimeout(t);
	columnTimers = [];
}

function resetData() {
	clearTimeout(idleTimer);
	clearColumnTimers();
	for (const period of ['current', 'previous']) {
		totals[period] = {};
		totalState[period] = {};
		series[period] = {};
		seriesState[period] = {};
	}
	leaderboardData.metrics = {};
	leaderboardData.loaded = [];
	leaderboardData.loading = [];
	leaderboardData.failed = [];
	leaderboardData.state = 'loading';
	leaderboardData.error = false;
	for (const k of Object.keys(audienceData)) delete audienceData[k];
}

// Each panel renders as soon as its own request lands. "Settled" only gates
// how an empty response is interpreted: while other first-paint requests are
// still in flight an empty metric shows a neutral "--", afterwards it is
// either a genuinely quiet period (everything empty) or a failed metric.
const landingSettled = computed(() => {
	const settled = (st) => !!st && st !== 'loading';
	if (!LANDING_SERIES.every((m) => settled(seriesState.current[m]))) return false;
	if (!LANDING_TOTALS.every((m) => settled(totalState.current[m]))) return false;
	if (showLeaderboard.value && leaderboardData.state === 'loading') return false;
	return true;
});
const landingEmpty = computed(() => landingSettled.value && isEverythingEmpty());

function isEverythingEmpty() {
	const anySeries = LANDING_SERIES.some((m) => Array.isArray(series.current[m]) && series.current[m].length);
	const anyTotal = LANDING_TOTALS.some((m) => totals.current[m] !== null && totals.current[m] !== undefined);
	const anyLeaderboard = leaderboardData.state === 'ready';
	return !anySeries && !anyTotal && !anyLeaderboard;
}

// Did any landing metric report activity (> 0) for this window? Plays,
// unique viewers, watch time and peak viewers are all-or-nothing for the same
// items, so once one of them is positive an empty answer for another is a
// swallowed upstream error, not "no data".
const landingActivity = computed(() => {
	if (LANDING_SERIES.some((m) => Array.isArray(series.current[m]) && seriesSum(series.current[m]) > 0)) return true;
	if (LANDING_TOTALS.some((m) => Number(totals.current[m]) > 0)) return true;
	return leaderboardData.state === 'ready' && Object.values(leaderboardData.metrics).some((row) => Object.values(row).some((v) => Number(v) > 0));
});
// A quiet window: the landing settled, something answered (so Bitmovin was
// reachable) and nothing reported activity. Every `[]` then means zero.
const quietWindow = computed(() => landingSettled.value && !landingEmpty.value && !landingActivity.value);
const quietEmptyCopy = computed(() => (quietWindow.value
	? { title: 'No activity in this period', text: 'No plays were recorded for the selected content and dates.' }
	: { title: 'No analytics for this period', text: 'No player activity was recorded for the selected content and dates. Try a different date range or selection.' }));

// "empty" from one panel is only meaningful once the landing has settled.
// Core answers `[]` for "no rows" and for swallowed Bitmovin errors alike, so
// this is a best-effort reading (the real fix is core returning errors):
//   - everything empty, or a quiet window        -> empty
//   - other metrics show activity                 -> error (retry offered)
//   - `optional` breakdowns (captions, audio)     -> always empty; plays can
//     exist without any caption/audio-track usage
function panelState(st, { optional = false } = {}) {
	if (!st || st === 'loading') return 'loading';
	if (st === 'empty') {
		if (!landingSettled.value) return 'loading';
		if (optional || landingEmpty.value || quietWindow.value) return 'empty';
		return 'error';
	}
	return st;
}

watch(landingSettled, (settled) => {
	if (!settled) return;
	lastUpdated.value = Date.now();
	perfMark('landing-settled');
	if (!viewedTracked) {
		viewedTracked = true;
		track(EVENTS.ANALYTICS_VIEWED, { range: range.value.preset, scope: scope.value, items: effectiveIds.value.length });
	}
});

function baseParams(period = 'current') {
	const r = period === 'previous' ? prevRange.value : range.value;
	return { startTime: r.from, endTime: r.to, selectedStreams: effectiveIds.value };
}

// ------------------------------------------------------------ fetchers
async function loadSeries(metric, period = 'current', { force = false } = {}) {
	if (series[period][metric] && !force) return series[period][metric];
	const stamp = filtersStamp();
	seriesState[period][metric] = 'loading';
	try {
		const { data } = await fetchAnalytics({ ...baseParams(period), type: metric, interval: interval.value }, { force });
		if (stamp !== filtersStamp()) return null;
		if (!data.length) {
			series[period][metric] = null;
			seriesState[period][metric] = 'empty';
			return null;
		}
		const r = period === 'previous' ? prevRange.value : range.value;
		const filled = fillSeries(parseSeries(data), r.from, r.to, intervalMs(interval.value));
		series[period][metric] = filled;
		seriesState[period][metric] = 'ready';
		if (period === 'current') {
			perfMark('first-kpi');
			if (metricDependencies(trendMetric.value).includes(metric)) perfMark('trend');
		}
		return filled;
	} catch (e) {
		if (stamp !== filtersStamp()) return null;
		series[period][metric] = null;
		seriesState[period][metric] = 'error';
		return null;
	}
}

async function loadTotal(metric, period = 'current', { force = false } = {}) {
	if (totals[period][metric] !== undefined && !force) return totals[period][metric];
	const stamp = filtersStamp();
	totalState[period][metric] = 'loading';
	try {
		const { data } = await fetchAnalytics({ ...baseParams(period), type: metric }, { force });
		if (stamp !== filtersStamp()) return null;
		const value = parseTotal(data);
		totals[period][metric] = value;
		totalState[period][metric] = value === null ? 'empty' : 'ready';
		if (period === 'current' && value !== null) perfMark('first-kpi');
		return value;
	} catch (e) {
		if (stamp !== filtersStamp()) return null;
		totals[period][metric] = null;
		totalState[period][metric] = 'error';
		return null;
	}
}

// Core answers `[]` both for "no rows" and for a swallowed Bitmovin error
// (rate limit, timeout). For a secondary column we can tell them apart: when
// the watch-time column has rows, plays / unique viewers / peak viewers cannot
// all be empty for those same items, so `[]` is a failure. Retry once with a
// cache bypass, then surface a retry control in the column header.
function markColumnFailed(metric, stamp, { autoRetry }) {
	if (!leaderboardData.failed.includes(metric)) leaderboardData.failed = [...leaderboardData.failed, metric];
	if (!autoRetry) return;
	columnTimers.push(setTimeout(() => {
		if (stamp !== filtersStamp() || !leaderboardData.failed.includes(metric)) return;
		loadLeaderboardColumn(metric, { force: true, autoRetry: false });
	}, LEADERBOARD_RETRY_MS));
}

async function loadLeaderboardColumn(metric, { force = false, autoRetry = true } = {}) {
	if (leaderboardData.loaded.includes(metric) && !force) return;
	if (leaderboardData.loading.includes(metric)) return;
	const stamp = filtersStamp();
	leaderboardData.loading = [...leaderboardData.loading, metric];
	leaderboardData.failed = leaderboardData.failed.filter((m) => m !== metric);
	const isPrimary = metric === LEADERBOARD_PRIMARY_COLUMN;
	try {
		const { data } = await fetchAnalytics({ ...baseParams('current'), type: metric, groupBy: 'VIDEO_ID' }, { force });
		if (stamp !== filtersStamp()) return;
		const grouped = parseGrouped(data);
		if (!grouped.length && !isPrimary && leaderboardData.state === 'ready') {
			markColumnFailed(metric, stamp, { autoRetry });
			return;
		}
		const next = { ...leaderboardData.metrics };
		for (const { key, value } of grouped) {
			const id = normalizeContentId(key);
			next[id] = { ...(next[id] || {}), [metric]: value };
		}
		// Items with no rows genuinely had no activity for this metric.
		if (grouped.length) {
			for (const id of effectiveIds.value) {
				if (next[id]?.[metric] === undefined) next[id] = { ...(next[id] || {}), [metric]: 0 };
			}
		}
		leaderboardData.metrics = next;
		leaderboardData.loaded = [...leaderboardData.loaded, metric];
		if (isPrimary) {
			leaderboardData.state = grouped.length ? 'ready' : 'empty';
			if (grouped.length) {
				perfMark('leaderboard');
				// A sort the user chose earlier survives filter changes; fetch that
				// column right away instead of showing an unsorted table until idle.
				const sortColumn = leaderboardSort.value.column;
				if (sortColumn !== LEADERBOARD_PRIMARY_COLUMN) loadLeaderboardColumns(sortColumn);
				// The remaining columns follow once the table has been on screen
				// for a moment, off the critical path of the first paint.
				clearTimeout(idleTimer);
				idleTimer = setTimeout(() => {
					if (stamp !== filtersStamp()) return;
					if (typeof document !== 'undefined' && document.hidden) return;
					loadLeaderboardSecondary();
				}, LEADERBOARD_IDLE_MS);
			}
		}
	} catch (e) {
		if (stamp !== filtersStamp()) return;
		if (isPrimary) {
			leaderboardData.state = 'error';
			leaderboardData.error = true;
		} else {
			markColumnFailed(metric, stamp, { autoRetry });
		}
	} finally {
		if (stamp === filtersStamp()) leaderboardData.loading = leaderboardData.loading.filter((m) => m !== metric);
	}
}

async function loadAudience(tabKey, { force = false } = {}) {
	const tab = AUDIENCE_TAB_BY_KEY[tabKey];
	if (!tab) return;
	if (audienceData[tabKey]?.state === 'ready' && !force) return;
	const stamp = filtersStamp();
	audienceData[tabKey] = { rows: [], total: 0, state: 'loading' };
	try {
		const { data } = await fetchAnalytics({ ...baseParams('current'), type: tab.type, groupBy: tab.groupBy }, { force });
		if (stamp !== filtersStamp()) return;
		const grouped = parseGrouped(data).map((g) => ({ ...g, label: audienceLabel(tabKey, g.key), title: g.key ?? '' }));
		// Merge rows that map to the same label (e.g. several unknown markers).
		const merged = new Map();
		for (const g of grouped) {
			const existing = merged.get(g.label);
			if (existing) existing.value += g.value;
			else merged.set(g.label, { ...g });
		}
		const rows = Array.from(merged.values()).sort((a, b) => b.value - a.value);
		const { rows: top, total } = topWithOther(rows, 8);
		audienceData[tabKey] = { rows: top, total, state: rows.length ? 'ready' : 'empty' };
		if (rows.length) perfMark('audience');
	} catch (e) {
		if (stamp !== filtersStamp()) return;
		audienceData[tabKey] = { rows: [], total: 0, state: 'error' };
	}
}

function audienceLabel(tabKey, key) {
	const tab = AUDIENCE_TAB_BY_KEY[tabKey];
	if (tabKey === 'country') {
		if (key === null || key === undefined || key === '?' || key === '') return 'Unknown';
		return countryNames.value[String(key).toUpperCase()] || String(key);
	}
	if (tabKey === 'captions' || tabKey === 'audio') return formatLanguage(key, tab.emptyLabel);
	if (tabKey === 'device') return formatDeviceClass(key);
	return formatDimensionValue(key);
}

// Filters signature used to drop stale responses without AbortController
// (cancelling does not save Bitmovin quota once the request has left).
function filtersStamp() {
	return JSON.stringify([range.value.from.getTime(), range.value.to.getTime(), effectiveIds.value]);
}

// ------------------------------------------------------------- landing
// Budget (Compare off): plays series, watch-time total, leaderboard watch
// time, unique-viewers total, concurrent series = 5, plus the active audience
// tab right after the first paint = 6. Avg. view time is derived.
//
// Every request is dispatched at once (the browser sends them in call order,
// so the most valuable ones go first) and each panel renders the moment its
// own response lands; nothing waits on the whole batch.
function loadLanding({ force = false } = {}) {
	if (!effectiveIds.value.length) return Promise.resolve();
	resetData();
	perfReset();
	perfMark('landing-start');
	const stamp = filtersStamp();
	landingStamp = stamp;
	const tasks = [
		loadSeries('play', 'current', { force }),
		loadTotal('totalHoursWatched', 'current', { force }),
	];
	if (showLeaderboard.value) tasks.push(loadLeaderboardColumn(LEADERBOARD_PRIMARY_COLUMN, { force }));
	tasks.push(loadTotal('uniqueUsers', 'current', { force }));
	tasks.push(loadSeries('concurrentViewers', 'current', { force }));
	for (const dep of trendSeriesDependencies()) tasks.push(loadSeries(dep, 'current', { force }));
	if (compare.value) tasks.push(loadComparison({ force }));

	// Audience follows the first paint of the skeletons, not the first data.
	nextTick(() => {
		const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 0);
		raf(() => {
			if (stamp !== filtersStamp()) return;
			loadAudience(audienceTab.value, { force });
		});
	});

	return Promise.allSettled(tasks);
}

// Series the trend panel needs beyond what the KPI row already fetches.
function trendSeriesDependencies() {
	return metricDependencies(trendMetric.value).filter((m) => !LANDING_SERIES.includes(m));
}

// Compare on adds: plays + concurrent previous series (2), unique/watch
// previous totals (2), plus the previous series of the active trend tab when
// it is not one of those two (1) = 4 or 5.
function loadComparison({ force = false } = {}) {
	const tasks = [
		loadSeries('play', 'previous', { force }),
		loadTotal('totalHoursWatched', 'previous', { force }),
		loadTotal('uniqueUsers', 'previous', { force }),
		loadSeries('concurrentViewers', 'previous', { force }),
	];
	for (const dep of trendSeriesDependencies()) tasks.push(loadSeries(dep, 'previous', { force }));
	return Promise.allSettled(tasks);
}

// Fetch the grouped requests behind one (possibly derived) column.
function loadLeaderboardColumns(columnKey) {
	const col = LEADERBOARD_COLUMNS.find((c) => c.key === columnKey);
	for (const dep of metricDependencies(col ? col.metric : columnKey)) loadLeaderboardColumn(dep);
}

// Secondary columns go out in value order, a beat apart, so three Bitmovin
// queries don't land on the shared licence in the same instant. Columns that
// failed earlier are re-requested with a cache bypass.
function loadLeaderboardSecondary(priorityColumn) {
	if (!showLeaderboard.value || leaderboardData.state !== 'ready') return;
	const stamp = filtersStamp();
	const col = LEADERBOARD_COLUMNS.find((c) => c.key === priorityColumn);
	const first = col ? metricDependencies(col.metric) : [];
	const pending = [...first, ...LEADERBOARD_REQUEST_COLUMNS.filter((m) => !first.includes(m))]
		.filter((m) => m !== LEADERBOARD_PRIMARY_COLUMN
			&& !leaderboardData.loaded.includes(m) && !leaderboardData.loading.includes(m));
	pending.forEach((metric, i) => {
		const start = () => {
			if (stamp !== filtersStamp()) return;
			loadLeaderboardColumn(metric, { force: leaderboardData.failed.includes(metric) });
		};
		if (i === 0) start();
		else columnTimers.push(setTimeout(start, i * LEADERBOARD_STAGGER_MS));
	});
}

// Header retry for a column whose grouped request failed.
function retryLeaderboardColumn(columnKey) {
	const col = LEADERBOARD_COLUMNS.find((c) => c.key === columnKey);
	for (const dep of metricDependencies(col ? col.metric : columnKey)) {
		if (!leaderboardData.loaded.includes(dep)) loadLeaderboardColumn(dep, { force: true, autoRetry: false });
	}
}

// -------------------------------------------------------------- KPIs
// Plays and peak concurrent viewers are derived from the series that the
// landing already needs (sum / max), unique viewers and watch time come from
// one no-interval request each, and avg. view time = watch time / plays.
const SERIES_BACKED_KPIS = ['play', 'concurrentViewers'];

function kpiValue(metric, period = 'current') {
	if (metric === 'avgViewTime') {
		const watch = kpiValue('totalHoursWatched', period);
		const plays = kpiValue('play', period);
		if (watch === undefined || plays === undefined) return undefined;
		return avgViewTimeMs(watch, plays);
	}
	if (SERIES_BACKED_KPIS.includes(metric)) {
		const s = series[period][metric];
		if (!Array.isArray(s)) return s === null ? null : undefined;
		return metric === 'concurrentViewers' ? seriesMax(s) : seriesSum(s);
	}
	return totals[period][metric];
}

function kpiState(metric, period = 'current') {
	if (metric === 'avgViewTime') return combineStates(kpiState('totalHoursWatched', period), kpiState('play', period));
	if (SERIES_BACKED_KPIS.includes(metric)) return seriesState[period][metric];
	return totalState[period][metric];
}

// Worst-of for derived values: loading > error > empty > ready.
function combineStates(...states) {
	if (states.some((s) => !s || s === 'loading')) return 'loading';
	if (states.some((s) => s === 'error')) return 'error';
	if (states.some((s) => s === 'empty')) return 'empty';
	return 'ready';
}

function kpiDisplay(metric) {
	const state = kpiState(metric, 'current');
	let value = kpiValue(metric, 'current');
	const loading = state === 'loading' || state === undefined;
	// [] from core is ambiguous: when other metrics show activity, treat it
	// as a failed metric; in a quiet window (something answered, nothing > 0)
	// it is a zero, so all five cards agree instead of mixing 0 and "--".
	const error = !loading && (state === 'error' || (state === 'empty' && landingSettled.value && !landingEmpty.value && !quietWindow.value));
	if (state === 'empty' && quietWindow.value) value = 0;
	const display = loading || error || value === null || value === undefined ? '--' : formatMetricValue(metric, value);
	const prevState = kpiState(metric, 'previous');
	const prevValue = kpiValue(metric, 'previous');
	const compareLoading = compare.value && (prevState === 'loading' || prevState === undefined);
	let sparkline = null;
	const s = series.current[metric];
	if (Array.isArray(s) && s.length >= 2) sparkline = s.map((p) => p[1]);
	return {
		value: display,
		title: value !== null && value !== undefined ? (METRIC_BY_KEY[metric].unit === 'count' ? formatNumber(value) : formatDurationHms(value)) : '',
		loading,
		error,
		sparkline,
		compareLoading,
		delta: compare.value && !compareLoading ? formatDelta(value, prevValue ?? 0) : null,
	};
}

function retryKpi(metric) {
	for (const dep of metricDependencies(metric)) {
		const load = SERIES_BACKED_KPIS.includes(dep) ? loadSeries : loadTotal;
		if (compare.value) load(dep, 'previous', { force: true });
		load(dep, 'current', { force: true });
	}
}

// -------------------------------------------------------------- trend
// Avg. view time has no series of its own: it is watch time / plays per
// bucket, so the tab fetches the watch-time series and reuses the plays one.
function trendSeriesFor(period) {
	const metric = trendMetric.value;
	if (metric === 'avgViewTime') {
		const watch = series[period].totalHoursWatched;
		const plays = series[period].play;
		return Array.isArray(watch) && Array.isArray(plays) ? deriveAvgViewTimeSeries(watch, plays) : [];
	}
	return Array.isArray(series[period][metric]) ? series[period][metric] : [];
}
const trendSeries = computed(() => {
	const previous = compare.value ? trendSeriesFor('previous') : [];
	return { current: trendSeriesFor('current'), previous: previous.length ? previous : null };
});
const trendState = computed(() => panelState(combineStates(...metricDependencies(trendMetric.value).map((m) => seriesState.current[m]))));
const trendMetricLabel = computed(() => METRIC_BY_KEY[trendMetric.value]?.trendLabel || METRIC_BY_KEY[trendMetric.value]?.label || '');

function setTrendMetric(metric) {
	if (metric === trendMetric.value) return;
	trendMetric.value = metric;
	updateQuery();
	for (const dep of metricDependencies(metric)) {
		loadSeries(dep, 'current');
		if (compare.value) loadSeries(dep, 'previous');
	}
}

function retryTrend() {
	for (const dep of metricDependencies(trendMetric.value)) {
		if (compare.value) loadSeries(dep, 'previous', { force: true });
		loadSeries(dep, 'current', { force: true });
	}
}

// --------------------------------------------------------- leaderboard
const leaderboardRows = computed(() => {
	if (!showLeaderboard.value) return [];
	const ids = new Set(effectiveIds.value);
	for (const id of Object.keys(leaderboardData.metrics)) ids.add(id);
	const rows = Array.from(ids).map((id) => {
		const info = resolveContent(contentIndex.value, id);
		// thumbnailUrl is a hook for posters; nothing in the content index sets it yet.
		return {
			id,
			name: info.name,
			nameResolving: isNameResolving(info),
			type: info.type,
			folderName: info.folderName || '',
			thumbnailUrl: info.thumbnailUrl || null,
			metrics: withDerivedMetrics(leaderboardData.metrics[id]),
		};
	});
	return sortLeaderboard(rows, leaderboardSort.value.column, leaderboardSort.value.direction);
});
const leaderboardState = computed(() => panelState(leaderboardData.state));
// Derived columns count as loaded/loading through their inputs.
const leaderboardLoadedColumns = computed(() => LEADERBOARD_COLUMNS
	.filter((c) => metricDependencies(c.metric).every((m) => leaderboardData.loaded.includes(m)))
	.map((c) => c.key));
const leaderboardLoadingColumns = computed(() => LEADERBOARD_COLUMNS
	.filter((c) => !leaderboardLoadedColumns.value.includes(c.key) && metricDependencies(c.metric).some((m) => leaderboardData.loading.includes(m)))
	.map((c) => c.key));
const leaderboardFailedColumns = computed(() => LEADERBOARD_COLUMNS
	.filter((c) => !leaderboardLoadedColumns.value.includes(c.key) && !leaderboardLoadingColumns.value.includes(c.key)
		&& metricDependencies(c.metric).some((m) => leaderboardData.failed.includes(m)))
	.map((c) => c.key));
// "Ranked by watch time · Showing your 5 live streams and 5 videos" while the
// default set is in use (with a Choose content link), otherwise the count.
const leaderboardSubtitle = computed(() => {
	const col = LEADERBOARD_COLUMNS.find((c) => c.key === leaderboardSort.value.column);
	const ranked = `Ranked by ${col ? col.label.toLowerCase() : 'watch time'}`;
	if (resolvingDefaults.value) return `${ranked} · Finding your most recent content…`;
	if (isDefaultSelection.value) {
		const mix = describeDefaultSelection(defaultMix.value);
		return mix ? `${ranked} · Showing ${mix}` : ranked;
	}
	const n = effectiveIds.value.length;
	return `${ranked} · ${n} item${n === 1 ? '' : 's'} selected`;
});
const leaderboardSubtitleLink = computed(() => (isDefaultSelection.value && !resolvingDefaults.value ? 'Choose content' : ''));

function onLeaderboardSort(sort) {
	leaderboardSort.value = sort;
}

function retryLeaderboard() {
	leaderboardData.state = 'loading';
	loadLeaderboardColumn(LEADERBOARD_PRIMARY_COLUMN, { force: true });
}

function openContent(row) {
	track(EVENTS.ANALYTICS_CONTENT_OPENED, { contentType: row.type, source: 'leaderboard' });
	router.push({
		name: 'AnalyticsDashboardWithRoute',
		params: { videoId: row.id },
		query: { ...route.query, ...(row.type === 'live' ? { stream: 'true' } : {}) },
		// Hand the title over so the banner never waits on the stores.
		state: row.nameResolving ? {} : { title: row.name, type: row.type },
	});
}

// ------------------------------------------------------------ audience
const audienceRows = computed(() => audienceData[audienceTab.value]?.rows || []);
const audienceTotal = computed(() => audienceData[audienceTab.value]?.total || 0);
const audienceState = computed(() => panelState(audienceData[audienceTab.value]?.state, { optional: !!AUDIENCE_TAB_BY_KEY[audienceTab.value]?.optional }));

function setAudienceTab(tab) {
	if (tab === audienceTab.value) return;
	audienceTab.value = tab;
	updateQuery();
	loadAudience(tab);
}

function retryAudience() {
	loadAudience(audienceTab.value, { force: true });
}

// --------------------------------------------------------- interactions
function scheduleReload() {
	clearTimeout(debounceTimer);
	debounceTimer = setTimeout(() => {
		range.value = resolveRange({ preset: range.value.preset, from: range.value.from, to: range.value.to, tz: timezone.value });
		updateQuery();
		loadLanding();
	}, FILTER_DEBOUNCE_MS);
}

function onRangeApply(payload) {
	if (payload.presetKey) {
		range.value = resolveRange({ preset: payload.presetKey, tz: timezone.value });
	} else {
		range.value = resolveRange({ preset: 'custom', from: payload.from, to: payload.to, tz: timezone.value });
	}
	zoomBaseRange.value = null; // an explicit pick replaces the zoom, nothing to go back to
	rangeMenuRef.value?.hide?.();
	scheduleReload();
}

// ---- drag-to-zoom on the trend chart
// A drag on the trend chart becomes a custom range for the whole dashboard
// (same path as the date picker: one debounced reload, no extra requests).
// The range in force before the first zoom is kept so "Back to …" is exact.
const zoomBaseRange = ref(null);
const zoomResetLabel = computed(() => {
	const base = zoomBaseRange.value;
	if (!base) return '';
	return base.preset === 'custom' ? 'Back to previous range' : `Back to ${rangeLabel(base)}`;
});

function onTrendZoom({ from, to }) {
	if (!(from instanceof Date) || !(to instanceof Date) || !(to > from)) return;
	if (!zoomBaseRange.value) zoomBaseRange.value = { ...range.value };
	// resolveRange clamps to now and the 30-day cap.
	range.value = resolveRange({ preset: 'custom', from, to, tz: timezone.value });
	scheduleReload();
}

function resetTrendZoom() {
	const base = zoomBaseRange.value;
	if (!base) return;
	zoomBaseRange.value = null;
	// Presets re-anchor at "now" like any other preset pick; a custom base keeps its instants.
	range.value = base.preset === 'custom'
		? resolveRange({ preset: 'custom', from: base.from, to: base.to, tz: timezone.value })
		: resolveRange({ preset: base.preset, tz: timezone.value });
	scheduleReload();
}

function toggleCompare() {
	compare.value = !compare.value;
	updateQuery();
	if (compare.value && effectiveIds.value.length && landingStamp) loadComparison();
}

async function setScope(next) {
	if (next === scope.value) return;
	scope.value = next;
	if (isDefaultSelection.value && !deepLinkId.value) {
		// The default set is per scope (Videos = recent files, not the live
		// half of the mixed set), so recompute before reloading.
		resetData();
		landingStamp = null;
		if (!(await applyDefaultSelection())) return;
	}
	scheduleReload();
}

function onPickerApply({ live, vod, vodFiles }) {
	cancelDefaultResolution();
	knownVodFiles.value = mergeVodFiles(knownVodFiles.value, vodFiles);
	selectedIds.value = [...live, ...vod].slice(0, MAX_SELECTED_CONTENT);
	isDefaultSelection.value = false;
	if (deepLinkId.value) {
		// Leaving the single-item route; the route watcher reloads with the
		// selection the user just made instead of the default set.
		keepSelectionOnRouteChange = true;
		router.replace({ name: 'AnalyticsDashboard', query: route.query });
		return;
	}
	scheduleReload();
}

function removeSelected(id) {
	cancelDefaultResolution();
	selectedIds.value = selectedIds.value.filter((x) => x !== id);
	isDefaultSelection.value = false;
	scheduleReload();
}

function clearSelection() {
	cancelDefaultResolution();
	selectedIds.value = [];
	isDefaultSelection.value = false;
	updateQuery();
	resetData();
	landingStamp = null;
}

function clearDeepLink() {
	// The route watcher resets the selection and reloads.
	router.replace({ name: 'AnalyticsDashboard', query: { ...route.query, scope: undefined, ids: undefined, stream: undefined, folderId: undefined } });
}

async function refresh() {
	if (isRefreshing.value) return;
	isRefreshing.value = true;
	clearAnalyticsCache();
	range.value = resolveRange({ preset: range.value.preset, from: range.value.from, to: range.value.to, tz: timezone.value });
	try {
		await loadLanding({ force: true });
	} finally {
		isRefreshing.value = false;
	}
}

function mergeVodFiles(existing, incoming = []) {
	const map = new Map(existing.map((f) => [vodFileKey(f), f]));
	for (const f of incoming) map.set(vodFileKey(f), f);
	return Array.from(map.values());
}

watch(timezone, (tz) => {
	storeTimeZone(tz);
	// Only "Today" depends on the calendar; other presets are rolling windows.
	if (range.value.preset === 'today') scheduleReload();
});

// ------------------------------------------------------------- URL sync
function updateQuery() {
	if (booting.value) return;
	const q = { ...route.query, ...rangeToQuery(range.value) };
	q.scope = scope.value === 'all' ? undefined : scope.value;
	q.compare = compare.value ? '1' : undefined;
	q.metric = trendMetric.value === DEFAULT_TREND_METRIC ? undefined : trendMetric.value;
	q.audience = audienceTab.value === AUDIENCE_TABS[0].key ? undefined : audienceTab.value;
	q.ids = !deepLinkId.value && !isDefaultSelection.value && selectedIds.value.length ? selectedIds.value.join(',') : undefined;
	// Folders of the selected videos, so a reload can name them with one
	// playlist request per folder instead of sweeping the library.
	const folders = q.ids ? Array.from(new Set(selectedItems.value.map((i) => i.folderId).filter(Boolean))) : [];
	// While the restored videos are still being looked up the hint is all we
	// have, so keep it rather than dropping it from the URL.
	q.folders = folders.length ? folders.join(',') : q.ids && vodNamesPending.value ? q.folders : undefined;
	const cleaned = Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== ''));
	if (JSON.stringify(cleaned) !== JSON.stringify(route.query)) router.replace({ query: cleaned });
}

function readQuery() {
	const q = route.query;
	range.value = rangeFromQuery(q, { tz: timezone.value });
	zoomBaseRange.value = null;
	scope.value = SCOPES.some((s) => s.key === q.scope) ? q.scope : 'all';
	compare.value = q.compare === '1';
	trendMetric.value = METRIC_BY_KEY[q.metric] ? q.metric : DEFAULT_TREND_METRIC;
	audienceTab.value = AUDIENCE_TAB_BY_KEY[q.audience] ? q.audience : AUDIENCE_TABS[0].key;
	if (isValidTimeZone(q.tz)) timezone.value = q.tz;
	if (queryHasIds()) {
		selectedIds.value = q.ids.split(',').map((s) => s.trim()).filter(Boolean).slice(0, MAX_SELECTED_CONTENT);
		isDefaultSelection.value = false;
	}
}

function queryHasIds() {
	return typeof route.query.ids === 'string' && !!route.query.ids.trim();
}

// Videos restored from the URL are only refIds until their folder playlist
// has been read (the picker and the default set hand the files over
// directly). Names show a skeleton meanwhile, never "Untitled video".
let vodLookupToken = 0;
async function resolveSelectedVodNames(streamsReady = Promise.resolve()) {
	const token = ++vodLookupToken;
	const pendingIds = () => selectedIds.value.filter((id) => !id.startsWith('live_') && resolveContent(contentIndex.value, id).unresolved);
	if (!pendingIds().length) return;
	vodNamesPending.value = true;
	try {
		await streamsReady;
		const ids = pendingIds();
		if (token !== vodLookupToken || !ids.length) return;
		const folderIds = typeof route.query.folders === 'string' ? route.query.folders.split(',').map((f) => f.trim()).filter(Boolean) : [];
		const files = await findVodFiles(ids, vodFolders.value, { folderIds });
		if (token !== vodLookupToken) return;
		if (files.length) knownVodFiles.value = mergeVodFiles(knownVodFiles.value, files);
	} catch (e) {
		// Whatever stayed unresolved falls back to "Untitled video".
	} finally {
		if (token === vodLookupToken) vodNamesPending.value = false;
	}
}

// -------------------------------------------------------------- export
const canExport = computed(() => !!lastUpdated.value && effectiveIds.value.length > 0);

function csvValue(metric, value) {
	if (value === null || value === undefined) return '';
	return METRIC_BY_KEY[metric].unit === 'duration' ? formatDurationHms(value) : Math.round(value);
}

function exportCsv(sections) {
	const tz = timezone.value;
	const stamp = `${formatInTz(range.value.from, tz, 'iso')} to ${formatInTz(range.value.to, tz, 'iso')} (${tz})`;
	const rows = [];
	if (sections.includes('kpis')) {
		for (const m of METRICS) {
			const row = { Section: 'Summary', Metric: m.label, Period: stamp, Value: csvValue(m.key, kpiValue(m.key, 'current')) };
			if (compare.value) {
				row['Previous value'] = csvValue(m.key, kpiValue(m.key, 'previous'));
				const delta = formatDelta(kpiValue(m.key, 'current'), kpiValue(m.key, 'previous') ?? 0);
				row['Change'] = delta.kind === 'up' ? `+${delta.label}` : delta.kind === 'down' ? `-${delta.label}` : delta.label;
			}
			rows.push(row);
		}
	}
	if (sections.includes('trend')) {
		const cur = trendSeries.value.current;
		const prev = trendSeries.value.previous;
		cur.forEach(([ts, v], i) => {
			const row = { Section: 'Trend', Metric: trendMetricLabel.value, Period: formatBucketIso(ts, interval.value, tz), Value: csvValue(trendMetric.value, v) };
			if (prev) {
				row['Previous period'] = prev[i] ? formatBucketIso(prev[i][0], interval.value, tz) : '';
				row['Previous value'] = prev[i] ? csvValue(trendMetric.value, prev[i][1]) : '';
			}
			rows.push(row);
		});
	}
	if (sections.includes('leaderboard')) {
		leaderboardRows.value.forEach((r, i) => {
			const row = { Section: 'Top content', Rank: i + 1, Content: r.name, Type: r.type === 'live' ? 'Live' : 'Video', Id: r.id };
			for (const col of LEADERBOARD_COLUMNS) row[col.label] = csvValue(col.metric, r.metrics[col.key]);
			rows.push(row);
		});
	}
	if (!rows.length) return;
	csvDownload({ data: normalizeCsvRows(rows), filename: `castr-analytics-${range.value.preset}-${formatInTz(Date.now(), tz, 'iso').replace(/[: ]/g, '-')}` });
	track(EVENTS.ANALYTICS_EXPORTED, { range: range.value.preset, items: effectiveIds.value.length, sections: sections.join('|'), metric: trendMetric.value });
}

function exportAudienceCsv() {
	const tab = AUDIENCE_TAB_BY_KEY[audienceTab.value];
	const data = audienceRows.value.map((r) => ({ [tab.label]: r.label, Plays: r.value, Share: `${(r.share * 100).toFixed(1)}%` }));
	if (!data.length) return;
	csvDownload({ data, filename: `castr-analytics-audience-${tab.key}-${range.value.preset}` });
	track(EVENTS.ANALYTICS_EXPORTED, { range: range.value.preset, items: effectiveIds.value.length, sections: `audience:${tab.key}` });
}

// json-to-csv-export derives headers from the first row; make every row
// carry the union of keys so mixed sections line up.
function normalizeCsvRows(rows) {
	const keys = [];
	for (const r of rows) for (const k of Object.keys(r)) if (!keys.includes(k)) keys.push(k);
	return rows.map((r) => Object.fromEntries(keys.map((k) => [k, r[k] ?? ''])));
}

// -------------------------------------------------------------- header
const updatedAgoLabel = computed(() => formatUpdatedAgo(lastUpdated.value, nowTick.value));

// ---------------------------------------------------------------- boot
// Select the deep-linked item immediately (the id alone is enough to query
// Bitmovin); its name, type and scope are refined once the stores arrive.
// A title passed by the caller (router state from the leaderboard, or ?title=
// from another page opening a new tab) seeds the name; no skeleton needed.
function seedDeepLinkName(id) {
	const state = typeof window !== 'undefined' ? window.history?.state || {} : {};
	const title = typeof state.title === 'string' && state.title.trim() ? state.title.trim() : typeof route.query.title === 'string' ? route.query.title.trim() : '';
	if (!title) return;
	const type = state.type === 'live' || state.type === 'vod' ? state.type : route.query.stream === 'true' ? 'live' : id.startsWith('live_') ? 'live' : 'vod';
	seededNames.value = { ...seededNames.value, [id]: { name: title, type } };
}

function applyDeepLinkSelection() {
	const id = deepLinkId.value;
	if (!id) return;
	cancelDefaultResolution();
	seedDeepLinkName(id);
	selectedIds.value = [id];
	isDefaultSelection.value = false;
	applyDeepLinkScope();
	track(EVENTS.ANALYTICS_CONTENT_OPENED, { contentType: deepLinkItem.value?.type, source: 'deep_link' });
}

function applyDeepLinkScope() {
	if (!deepLinkItem.value) return;
	const { type } = deepLinkItem.value;
	scope.value = type === 'vod' ? 'vod' : type === 'live' ? 'live' : 'all';
}

async function resolveDeepLinkName(streamsReady) {
	const raw = route.params.videoId;
	if (!raw) return;
	const id = normalizeContentId(raw);
	const isVodLookup = String(raw).includes('.mp4') || !!route.query.folderId;
	if (isVodLookup) vodNamesPending.value = true;
	try {
		if (String(raw).includes('.mp4')) {
			const meta = await StreamService.getStreamPlaylistVideoMetaData(raw);
			if (meta) knownVodFiles.value = mergeVodFiles(knownVodFiles.value, [meta]);
		} else if (route.query.folderId) {
			const [files] = await Promise.all([StreamService.getStreamPlaylist(route.query.folderId), streamsReady]);
			const folder = vodFolders.value.find((f) => f._id === route.query.folderId);
			const match = (files || []).find((f) => vodFileKey(f) === id);
			if (match) knownVodFiles.value = mergeVodFiles(knownVodFiles.value, [{ ...match, playlistName: folder?.name, playlistId: folder?._id }]);
		}
	} catch (e) {
		// The banner falls back to "Untitled …".
	} finally {
		if (isVodLookup) vodNamesPending.value = false;
	}
	if (deepLinkId.value === id) applyDeepLinkScope();
}

onMounted(async () => {
	store.dispatch('Ui/setLoadingStatus', false);
	perfMark('mounted');

	// Kick off the prerequisites but do not wait for them: the User store only
	// decides the plan gate and the Streams store only supplies names (and
	// the default selection when the URL carries none).
	const userReady = loadUserStore();
	const streamsReady = loadStreamsStore();
	loadCountryNames();
	tickTimer = setInterval(() => { nowTick.value = Date.now(); }, 30000);

	readQuery();
	if (deepLinkId.value) {
		applyDeepLinkSelection();
		resolveDeepLinkName(streamsReady);
	} else if (selectedIds.value.length) {
		resolveSelectedVodNames(streamsReady);
	}
	if (!selectedIds.value.length) {
		// No ids in the URL: the default set is the most recent content in
		// scope, which needs the Streams store (and, for videos, the recent
		// folders' playlists — Castr API, resolved before the first Bitmovin
		// call so the landing budget is spent exactly once).
		booting.value = false;
		resolvingDefaults.value = true;
		await streamsReady;
		if (!(await applyDefaultSelection())) return;
	}
	booting.value = false;
	if (effectiveIds.value.length) {
		updateQuery();
		loadLanding();
	}
	await userReady;
});

async function loadUserStore() {
	try {
		await store.dispatch('User/loadUser');
	} catch (e) {
		// The gate falls back to whatever the store already has.
	}
	userLoaded.value = true;
	perfMark('user-loaded');
}

async function loadStreamsStore() {
	try {
		await store.dispatch('Streams/getStreams');
	} catch (e) {
		// Names stay as raw ids; the default selection is empty.
	}
	perfMark('streams-loaded');
}

// The scope filter and content names depend on the Streams store; when it
// lands after the first requests went out, re-query only if the effective
// selection actually changed (e.g. URL ids filtered by scope).
watch(streamsLoaded, (loaded) => {
	if (!loaded || booting.value) return;
	if (landingStamp && filtersStamp() !== landingStamp) loadLanding();
});

async function loadCountryNames() {
	try {
		const list = await IntegrationService.getCountryList();
		const map = {};
		for (const c of list || []) if (c?.code) map[String(c.code).toUpperCase()] = c.name;
		countryNames.value = map;
		perfMark('countries-loaded');
		// Re-label an already loaded Country tab without another request.
		const loaded = audienceData.country;
		if (loaded?.state === 'ready') {
			loaded.rows = loaded.rows.map((r) => (r.isOther ? r : { ...r, label: audienceLabel('country', r.key) }));
		}
	} catch (e) {
		// Country codes stay as codes.
	}
}

// Route param changes while mounted (leaderboard row click, back button,
// "View all analytics").
let keepSelectionOnRouteChange = false;
watch(() => route.params.videoId, async (next, prev) => {
	if (booting.value || next === prev) return;
	if (next) {
		cancelDefaultResolution();
		applyDeepLinkSelection();
		resolveDeepLinkName(Promise.resolve());
	} else if (keepSelectionOnRouteChange) {
		keepSelectionOnRouteChange = false;
	} else {
		// Back from a detail view lands on the overview URL as it was, ids and
		// scope included; only an overview without ids gets the default set.
		resetData();
		landingStamp = null;
		readQuery();
		if (queryHasIds()) {
			cancelDefaultResolution();
			resolveSelectedVodNames();
		} else {
			selectedIds.value = [];
			if (!(await applyDefaultSelection())) return;
		}
	}
	range.value = resolveRange({ preset: range.value.preset, from: range.value.from, to: range.value.to, tz: timezone.value });
	updateQuery();
	loadLanding();
});

onBeforeUnmount(() => {
	clearTimeout(idleTimer);
	clearColumnTimers();
	clearTimeout(debounceTimer);
	clearInterval(tickTimer);
});
</script>
